import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormHelper } from '@forms';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@user';
import { __i18n } from '@localisation';
import { HttpHelperService } from '@http';
import { Config } from '@config';
import { MessagesService, Message, MessageType } from '@ui-messages';
import { StateService, States } from '@state';
import { ThemeService } from '@themes';

@Component({
    selector: 'registration',
    templateUrl: './registration.template.html',
    standalone: false
})
export class RegistrationComponent {
    constructor(
        private formBuilder: FormBuilder,
        private formHelper: FormHelper,
        private user: UserService,
        private httpHelper: HttpHelperService,
        private config: Config,
        private router: Router,
        private messageService: MessagesService,
        private stateService: StateService,
        private themeService: ThemeService,
        private route: ActivatedRoute
    ) {
        this.registrationForm = formBuilder.group({
            email: ['', Validators.required],
            activationKey: ['', Validators.required],
            tenantKey: [''],
            accept: ['', Validators.requiredTrue]
        });

        if (this.config.serverConfig && this.config.serverConfig.SIGNUP_EMAIL) {
            const signUpInfoJson = localStorage.getItem('signupData');
            let signUpInfoAssigned = false;
            if (signUpInfoJson) {
                try {
                    const signUpInfo = JSON.parse(signUpInfoJson);
                    if (signUpInfo.Email && signUpInfo.NewActivationKey && signUpInfo.TermsOfUse) {
                        signUpInfoAssigned = true;
                        this.registrationForm.controls.email.setValue(signUpInfo.Email);
                        this.registrationForm.controls.activationKey.setValue(signUpInfo.NewActivationKey);
                        this.registrationForm.controls.tenantKey.setValue(signUpInfo.TenantKey);
                        if(this.registrationForm.controls.accept){
                            this.registrationForm.controls.accept.setValue(signUpInfo.TermsOfUse);
                        }
                        this.register();
                    }
                } catch {
                    // ignored
                }
            }
            if (!signUpInfoAssigned) {
                this.registrationForm.controls.email.setValue(this.config.serverConfig.SIGNUP_EMAIL);
            }
        }
        localStorage.removeItem('signupData');

        this.route.queryParams.subscribe(params => {
            const converted: any = {};
            Object.keys(params).forEach((current) => {
                converted[current.toLocaleLowerCase()] = params[current];
            });
            if(converted.email){
                this.registrationForm.controls.email.setValue(converted.email);
            }
            if(converted.activationkey){
                this.registrationForm.controls.activationKey.setValue(converted.activationkey);
            }
        })


        this.themeService.data.subscribe((themeData: any) => {
            if(themeData) {
                if (themeData.showSignUpTenantKey) {
                    this.showTenantKey = true;
                }
                if(themeData.disableSignupButton){
                    this.disableSignupButton = true;
                }
                this.showTermsOfUse = !themeData.dontShowTermsOfUse;
                if(!this.showTermsOfUse){
                    this.registrationForm.removeControl('accept')
                }
                if (this.config.serverConfig.EXT_AUTH_TYPE && themeData.showExternalLogin) {
                    this.showExternalLogin = !this.config.serverConfig || !this.config.serverConfig.SIGNUP_EMAIL;
                    this.externalLoginLink = this.config.externaLoginLink + this.config.serverConfig.EXT_AUTH_TYPE;
                }
                if(themeData.disableExternalSignupButton) {
                    this.showExternalLogin = false;
                }
            }
        });
    }

    public showTermsOfUse: boolean = false;

    public registrationForm: FormGroup;

    public registrationError: string;

    public registerButtonState: string;

    public disableSignupButton: boolean = false;

    public showTenantKey: boolean = false;
    public showExternalLogin: boolean;
    public externalLoginLink: string;

    public useExternalLogin() {
        this.formHelper.markFormGroupTouched(this.registrationForm);

        if (this.registrationForm.valid) {
            const registerData : any = {
                Email: this.registrationForm.controls.email.value,
                NewActivationKey: this.registrationForm.controls.activationKey.value,
                TenantKey: this.registrationForm.controls.tenantKey.value
            }
            if(this.showTermsOfUse){
                registerData.TermsOfUse = this.registrationForm.controls.accept.value;
            }
            localStorage.setItem('signupData', JSON.stringify(registerData));
            location.href = this.externalLoginLink;
        }
    }

    public async register() {
        this.formHelper.markFormGroupTouched(this.registrationForm);

        if (this.registrationForm.valid) {
            try {
                this.registerButtonState = 'loading';
                const registerData : any = {
                    Email: this.registrationForm.controls.email.value,
                    NewActivationKey: this.registrationForm.controls.activationKey.value,
                    TenantKey: this.registrationForm.controls.tenantKey.value
                }
                if(this.showTermsOfUse){
                    registerData.TermsOfUse = this.registrationForm.controls.accept.value;
                }
                const result = await this.user.register(registerData);
                this.stateService.change(States.userMustRegister, false);
                this.registerButtonState = 'loaded';
                if (this.config.serverConfig.SIGNUP_EMAIL) {
                    this.router.navigateByUrl('/VerificationCode');
                }else{
                    this.messageService.addMessage({
                        text: __i18n(
                            'The registration was successful. As a new user please check your e-mail account or log-in directly and finish the registration of your plant.',
                            'landingpage.register.success.message'
                        ),
                        type: MessageType.success,
                        target: 'landingpage'
                    });
                    this.router.navigateByUrl('/Login');
                }
            } catch (e) {
                this.registerButtonState = 'loaded';
                console.error('Error on registration! [RegistrationComponent.register]', e);
                this.registrationError =
                    this.httpHelper.getErrorMessage(e) ||
                    __i18n('Could not register, an error occured.', 'landingpage.register.error');
            }
        } else {
            this.registrationError = '';
        }
    }
}
